/**
 * Login intro (FATH TIME Logistics, 2026-10-25): muvaffaqiyatli kirishdan keyin to'q ko'k fonda lochin chapdan uchib kiradi
 * (ortida so'nuvchi iz), keskin to'xtaydi; qanotdagi ko'k chiziq tanadan dumga yonadi; FATH TIME harflari sirg'alib kiradi,
 * LOGISTICS markazdan yoyiladi; bitta yorug'lik o'tadi. So'ng ko'k tezlik pardasi o'tadi va uning ortidan platforma
 * ochiladi (AppShell: .app-reveal). Platforma shu vaqt ichida ostida yuklanib turadi.
 * Holat: features/auth/intro.ts; animatsiya: login-intro.css (vaqtlar Remotion'dagi FathTimeReveal bilan bir xil).
 */
import { useEffect, useId, type CSSProperties } from "react";
import { cn } from "@/lib/utils";
import { advanceIntro, introDuration, prefersReducedMotion, skipIntroLogo, useIntroPhase } from "@/features/auth/intro";
import { HORIZONTAL as L, STRIPE_STOPS, WHITE } from "./fath-time-logo";
import "./login-intro.css";

const ACTIVE_CLASS = "fath-intro-active";

function bbox(d: string) {
  const n = (d.match(/-?\d+\.?\d*/g) ?? []).map(Number);
  const xs = n.filter((_, i) => i % 2 === 0);
  const ys = n.filter((_, i) => i % 2 === 1);
  return { x0: Math.min(...xs), y0: Math.min(...ys), x1: Math.max(...xs), y1: Math.max(...ys) };
}

const [CX0, CY0, CX1, CY1] = L.content;
const VIEWBOX = `${CX0 - 2} ${CY0 - 2} ${CX1 - CX0 + 4} ${CY1 - CY0 + 4}`;
const BIRD = bbox(L.bird);
const STRIPE = bbox(L.stripe);
const SUB = bbox(L.subtitle.join(""));
// LOGISTICS: har harf markazdan o'z joyiga yoyiladi -- boshlang'ich siljish (logo birligida)
const SUB_SHIFT = L.subtitle.map((d) => {
  const b = bbox(d);
  return ((SUB.x0 + SUB.x1) / 2 - (b.x0 + b.x1) / 2) * 0.55;
});
// Yorug'lik: boshida va oxirida logodan tashqarida
const SHINE_FROM = BIRD.x0 - 70;
const SHINE_TO = CX1 + 30;

function Bird({ className, style }: { className: string; style?: CSSProperties }) {
  return (
    <g className={className} style={style}>
      <path d={L.bird} fill={WHITE} fillRule="evenodd" />
    </g>
  );
}

export function LoginIntro() {
  const phase = useIntroPhase();
  // Bir sahifada id'lar to'qnashmasin
  const uid = useId().replace(/[^a-zA-Z0-9_-]/g, "");
  const id = (name: string) => `fi-${name}-${uid}`;

  // Har bosqich vaqt bilan o'zi tugaydi: kirish hech qachon osilib qolmaydi
  useEffect(() => {
    if (phase === "idle") return;
    const timer = window.setTimeout(advanceIntro, introDuration(phase, prefersReducedMotion()));
    return () => window.clearTimeout(timer);
  }, [phase]);

  // Intro davomida sahifa aylanmaydi (kattalashgan platforma aylantirish chizig'ini chiqarmasin)
  useEffect(() => {
    if (phase === "idle") return;
    document.documentElement.classList.add(ACTIVE_CLASS);
    return () => document.documentElement.classList.remove(ACTIVE_CLASS);
  }, [phase]);

  // Esc, Enter yoki bo'sh joy -- logoni o'tkazib, darhol platformani ochadi
  useEffect(() => {
    if (phase !== "logo") return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" || e.key === "Enter" || e.key === " ") skipIntroLogo();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [phase]);

  if (phase === "idle") return null;
  return (
    // Bezak: ekran o'quvchilar uchun yashirin; ochilish bosqichida bosishlar platformaga o'tadi
    <div aria-hidden className={cn("fath-intro", phase === "reveal" && "is-reveal")} onClick={skipIntroLogo}>
      <div className="fath-intro__stage">
        <svg className="fath-intro__logo" viewBox={VIEWBOX}>
          <defs>
            <linearGradient id={id("stripe")} gradientUnits="userSpaceOnUse" x1={0} y1={0} x2={L.stripeX2} y2={0} gradientTransform={`matrix(${L.stripeMatrix})`}>
              {STRIPE_STOPS.map(([offset, color]) => (
                <stop key={offset} offset={offset} stopColor={color} />
              ))}
            </linearGradient>
            <linearGradient id={id("shine")} x1="0" x2="1" y1="0" y2="0">
              <stop offset="0" stopColor={WHITE} stopOpacity="0" />
              <stop offset="0.5" stopColor={WHITE} stopOpacity="0.55" />
              <stop offset="1" stopColor={WHITE} stopOpacity="0" />
            </linearGradient>
            <filter id={id("glow")} x="-50%" y="-200%" width="200%" height="500%">
              <feGaussianBlur stdDeviation="2.4" />
            </filter>
            {/* Ko'k chiziq tanadan (o'ng) dumga (chap) ochiladi */}
            <clipPath id={id("grow")}>
              <rect className="fi-grow" x={STRIPE.x0 - 2} y={STRIPE.y0 - 4} width={STRIPE.x1 - STRIPE.x0 + 4} height={STRIPE.y1 - STRIPE.y0 + 8} />
            </clipPath>
            {/* Yorug'lik faqat lochin va FATH TIME ustida */}
            <clipPath id={id("shine-clip")}>
              <path d={L.bird} clipRule="evenodd" />
              {L.title.map((d) => (
                <path key={d.slice(0, 16)} d={d} />
              ))}
            </clipPath>
          </defs>

          {/* Iz: lochinning bir necha kadr oldingi holatlari, so'nib boradi */}
          <Bird className="fi-bird fi-ghost" style={{ "--k": 1 } as CSSProperties} />
          <Bird className="fi-bird fi-ghost" style={{ "--k": 2 } as CSSProperties} />
          <Bird className="fi-bird fi-ghost" style={{ "--k": 3 } as CSSProperties} />

          <g className="fi-bird fi-falcon">
            <path d={L.bird} fill={WHITE} fillRule="evenodd" />
            <g clipPath={`url(#${id("grow")})`}>
              <path className="fi-glow" d={L.stripe} fill={`url(#${id("stripe")})`} fillRule="evenodd" filter={`url(#${id("glow")})`} />
              <path d={L.stripe} fill={`url(#${id("stripe")})`} fillRule="evenodd" />
            </g>
          </g>

          {L.title.map((d, i) => (
            // Harflar uchish yo'nalishida qiyshiq holda navbat bilan sirg'alib kiradi
            <path key={d.slice(0, 16)} d={d} fill={WHITE} fillRule="evenodd" className="fi-letter" style={{ "--i": i } as CSSProperties} />
          ))}
          {L.subtitle.map((d, i) => (
            <path key={d.slice(0, 16)} d={d} fill={WHITE} className="fi-tag" style={{ "--dx": `${SUB_SHIFT[i]}px` } as CSSProperties} />
          ))}

          <g clipPath={`url(#${id("shine-clip")})`}>
            <rect
              className="fi-shine"
              y={BIRD.y0 - 10}
              width={46}
              height={BIRD.y1 - BIRD.y0 + 20}
              fill={`url(#${id("shine")})`}
              style={{ "--from": `${SHINE_FROM}px`, "--to": `${SHINE_TO}px` } as CSSProperties}
            />
          </g>
        </svg>
      </div>
      <div className="fath-intro__wipe">
        <span />
        <span />
        <span />
      </div>
    </div>
  );
}
